import { ChatUser } from '@equip.health/ui';
import * as linkify from 'linkifyjs';
import { DateTime } from 'luxon';

export const userOptionAlphabeticalSortCompareFunction = (
  a: AutoCompleteOption,
  b: AutoCompleteOption,
): number => a.primaryText.localeCompare(b.primaryText, 'en');

export const dateStringToUTC = (dateString: string): Date =>
  new Date(dateString.endsWith('Z') ? dateString : `${dateString}Z`);

export const removeUrlsFromText = (text: string, replaceWith = ''): string => {
  const linksFound = linkify.find(text, 'url');
  let parsed = text;
  for (const link of linksFound ?? []) {
    parsed = parsed.replace(link.value, replaceWith);
  }
  return parsed;
};

export const getValueFromQueryParams = (
  query: string,
  param: string,
): string => {
  let keyValuePairs: string[][] = [];
  let matchingKeyValue: string[];
  let matchingValue: string;
  let result = '';

  try {
    keyValuePairs = query
      ?.slice(1)
      .split('&')
      .map((qString) => qString.split('='));

    matchingKeyValue = keyValuePairs?.find(([key]) => key === param);

    matchingValue = matchingKeyValue?.[1];

    result = (matchingValue ?? '').replace(/%20/g, ' ');

    return result;
  } catch (e) {
    console.warn(
      `Unable to get value from query params: original query: ${query}, param: ${param}, keyValuePairs: ${keyValuePairs}, matchingKeyValue: ${matchingKeyValue}, matchingValue: ${matchingValue}, result: ${result}. ERROR: ${e.message}`,
    );
    return '';
  }
};

export const sortListByLastNameFromFullName = (
  a: ChatUser,
  b: ChatUser,
): number => {
  return a.nickname
    .split(' ')
    .pop()
    .localeCompare(b.nickname.split(' ').pop(), 'en');
};

/**
 * Returns an ISO string from either a date time object or a date string
 *
 * Supported formats:
 *   - ANY date time object
 *   - ANY valid date string, e.g. LL-dd-yyyy, yyyy-LL-ddTHH:mm:ssZ, yyyy-LL-ddTHH:mm:ss.SSSZ
 */
export const toISO = (date: DateTime | string): string => {
  try {
    if (typeof date === 'string') {
      return new Date(date).toISOString();
    }
    return date.toJSDate().toISOString();
  } catch (e) {
    return null;
  }
};

/**
 * Returns an ISO string without milliseconds from either a date time object or a date string
 *
 * Supported formats:
 *   - ANY date time object
 *   - ANY valid date string, e.g. LL-dd-yyyy, yyyy-LL-ddTHH:mm:ssZ, yyyy-LL-ddTHH:mm:ss.SSSZ
 */
export const toISORemoveMillis = (date: DateTime | string): string => {
  try {
    return toISO(date).split('.')[0].concat('Z');
  } catch (e) {
    return null;
  }
};

export const getOrdinal = (day: number): string => {
  const lastDigit = day % 10;

  switch (lastDigit) {
    case 1:
      return day !== 11 ? 'st' : 'th';
    case 2:
      return day !== 12 ? 'nd' : 'th';
    case 3:
      return day !== 13 ? 'rd' : 'th';
    default:
      return 'th';
  }
};

/**
 * Formats a date string with ordinal indicator.
 *
 * @param dateString - The date string to be formatted. Supported formats:
 *   - ANY date time object
 *   - ANY valid date string, e.g. LL-dd-yyyy, yyyy-LL-ddTHH:mm:ssZ, yyyy-LL-ddTHH:mm:ss.SSSZ
 * @returns The formatted date string with ordinal indicator, e.g. '1st August'
 */
export const formatDateWithOrdinal = (dateString: string): string => {
  const date = DateTime.fromISO(dateString);
  const { day } = date;
  const month = date.toFormat('MMMM');
  const ordinal = getOrdinal(day);
  return `${day}${ordinal} ${month}`;
};

/**
 * Returns readable string of number of days
 *
 * @param numberOfDays - It will accept number of Days
 * @returns The formatted string difference in days, e.g. '16 days', '1 day' etc.
 */

export const formatDaysToReadableString = (numberOfDays: number): string => {
  const readableDateInDays = Math.floor(Math.abs(numberOfDays));
  const dayOrDays = readableDateInDays === 1 ? 'day' : 'days';
  return `${readableDateInDays} ${dayOrDays}`;
};

export const setCookieFromUrlParam = (
  paramName: string,
  cookieName: string,
  days = 7,
) => {
  try {
    const params = new URLSearchParams(window.location.search);
    const paramValue = params.get(paramName);

    if (paramValue) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${cookieName}=${paramValue}; expires=${expires.toUTCString()}; path=/`;
      return true;
    }
    return false;
  } catch (error) {
    console.error(
      `Error setting cookie from URL param: ${paramName}, cookieName: ${cookieName}, days: ${days}. Error: ${error}`,
    );
    return false;
  }
};

export const isValidUUID = (id: string | undefined): boolean => {
  if (!id) return false;
  const uuidV4Regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidV4Regex.test(id.trim());
};
